// src/hooks/usePageHeader.ts
// Hook untuk mengambil data header dinamis dari database

import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface PageHeader {
  id: string;
  profile_id: string;
  page_type: string;
  title: string;
  subtitle?: string;
  description?: string;
  background_image_url?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface UsePageHeaderReturn {
  headerData: PageHeader | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const usePageHeader = (pageType: 'worksheet' | 'contact'): UsePageHeaderReturn => {
  const [headerData, setHeaderData] = useState<PageHeader | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHeaderData = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('page_headers')
        .select('*')
        .eq('page_type', pageType)
        .eq('is_active', true)
        .single();

      if (fetchError) {
        if (fetchError.code === 'PGRST116') {
          // No data found, create default header
          const defaultHeader = {
            page_type: pageType,
            title: pageType === 'worksheet' ? 'Worksheet Gratis' : 'Hubungi Saya',
            subtitle: pageType === 'worksheet' 
              ? 'Koleksi worksheet edukatif untuk anak-anak'
              : 'Mari berkolaborasi dan berbagi pengalaman',
            description: pageType === 'worksheet'
              ? 'Temukan berbagai worksheet gratis yang dirancang khusus untuk membantu perkembangan belajar anak-anak dengan cara yang menyenangkan dan interaktif.'
              : 'Jika Anda tertarik untuk berkolaborasi, menawarkan beasiswa, atau ingin mengetahui lebih lanjut tentang pencapaian saya, silakan hubungi melalui formulir di bawah ini.',
            is_active: true
          };
          setHeaderData(defaultHeader as PageHeader);
        } else {
          throw fetchError;
        }
      } else {
        setHeaderData(data);
      }
    } catch (err) {
      console.error('Error fetching page header:', err);
      setError(err instanceof Error ? err.message : 'Terjadi kesalahan saat memuat header');
      
      // Set default data on error
      const defaultHeader = {
        id: 'default',
        profile_id: 'default',
        page_type: pageType,
        title: pageType === 'worksheet' ? 'Worksheet Gratis' : 'Hubungi Saya',
        subtitle: pageType === 'worksheet' 
          ? 'Koleksi worksheet edukatif untuk anak-anak'
          : 'Mari berkolaborasi dan berbagi pengalaman',
        description: pageType === 'worksheet'
          ? 'Temukan berbagai worksheet gratis yang dirancang khusus untuk membantu perkembangan belajar anak-anak dengan cara yang menyenangkan dan interaktif.'
          : 'Jika Anda tertarik untuk berkolaborasi, menawarkan beasiswa, atau ingin mengetahui lebih lanjut tentang pencapaian saya, silakan hubungi melalui formulir di bawah ini.',
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      setHeaderData(defaultHeader);
    } finally {
      setLoading(false);
    }
  };

  const refetch = () => {
    fetchHeaderData();
  };

  useEffect(() => {
    fetchHeaderData();
  }, [pageType]);

  return {
    headerData,
    loading,
    error,
    refetch
  };
};